import i18n, { type i18n as I18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { enTranslation } from '../public/locales/translations.en.ts';
import { frTranslation } from '../public/locales/translations.fr.ts';
import {
  type AppTranslations,
  NAMESPACE,
  plTranslation,
} from '../public/locales/translations.pl.ts';

// Every language of the app (ISO 639-1 codes, also sent as Accept-Language)
export const APP_LANGUAGES = ['pl', 'en', 'fr'] as const;

export type AppLanguage = (typeof APP_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: AppLanguage = 'pl';

// Not i18next's default "i18nextLng": that key may still hold a browser language detected by an old app version
export const LANGUAGE_STORAGE_KEY = 'i18nLanguage';

const resources = {
  en: enTranslation,
  fr: frTranslation,
  pl: plTranslation,
} satisfies Record<AppLanguage, AppTranslations>;

const isAppLanguage = (code: string | undefined): code is AppLanguage =>
  APP_LANGUAGES.some(language => language === code);

export const getAppLanguage = (instance: Pick<I18n, 'language'> = i18n): AppLanguage => {
  const code = instance.language?.split('-')[0];
  return isAppLanguage(code) ? code : DEFAULT_LANGUAGE;
};

i18n.on('languageChanged', () => {
  document.documentElement.lang = getAppLanguage();
});

// Polish unless the user chose another language (?lng=en or the switcher); the browser language is deliberately ignored
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init<never>({
    defaultNS: NAMESPACE,
    detection: {
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      lookupQuerystring: 'lng',
      order: ['querystring', 'localStorage'],
    },
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      escapeValue: false,
    },
    ns: [NAMESPACE],
    resources,
    supportedLngs: [...APP_LANGUAGES],
  });

export default i18n;
